import { SecondaryRegularButton } from 'components/ReusableComponents/Button/RegularButtons'
import ConfirmationDialog from 'components/ReusableComponents/ConfirmationDialog'
import CustomHeader from 'components/ReusableComponents/HeaderTitles'
import CopyIcon from 'components/ReusableComponents/Icons/CopyIcon'
import LoadingIndicator from 'components/ReusableComponents/LoadingIndicator'
import Table, {
  Cell,
  Row,
  TitleCell,
  TitleRow
} from 'components/ReusableComponents/Table'
import Toggle from 'components/ReusableComponents/Toggle'
import EditGuestDialog from 'features/EditGuestDialog'
import BonusDialog from 'features/Subscribers/BonusDialog'
import useCopyText from 'hooks/useCopyText'
import { loyaltyIdNamesByParentChain } from 'main/loyaltyIdNamesByParentChain'
import moment from 'moment'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useToasts } from 'react-toast-notifications/dist/ToastProvider'
import subscribersApi from 'services/api/subscribers'
import { formatPhoneNumber } from 'utils/helpers'

import { ReactComponent as ExcelIcon } from './excel.svg'
import styles from './Subscribers.module.scss'

const Subscribers = () => {
  const [isSubscribersLoading, setIsSubscribersLoading] = useState(false)
  const [isSubsscribersErred, setIsSubsscribersErred] = useState(false)

  const [lastSubscribers, setLastSubscribers] = useState()

  const [isBonusDialogVisible, setIsBonusDialogVisible] = useState(false)
  const [activeSubscriber, setActiveSubscriber] = useState()

  const [isEditGuestDialogVisible, setIsEditGuestDialogVisible] = useState(false)
  const [isLoyaltyIdMissingDialogVisible, setisLoyaltyIdMissingDialogVisible] =
    useState(false)

  const parentChain = useSelector(s => s.hotelDetails.parentChainCode)

  const loyaltyIdName = loyaltyIdNamesByParentChain[parentChain] || 'Loyalty id'

  const [isShowingPendingBonus, setIsShowingPendingBonus] = useState(false)
  const [isShowingWithMissingInfo, setIsShowingWithMissingInfo] = useState(false)

  const [usesBonus, setUsesBonus] = useState(false)

  const copy = useCopyText()

  const { addToast } = useToasts()

  function copyToExcel() {
    const titleRow = [
      'Guest name',
      'Phone',
      'Email',
      'Added on',
      'Check-out date',
      'Loyalty id',
      'Is bonus applied'
    ]

    const results = getSortedResults(
      lastSubscribers
        .filter(s => (isShowingWithMissingInfo ? s.isAnyMissingInformation : true))
        .filter(s => (isShowingPendingBonus ? s.isBonusGiven === false : true))
    )

    const resAsArrays = results.map(r => [
      r.firstName ? `${r.firstName} ${r.lastName}` : 'Missing',
      formatPhoneNumber(r.phone),
      r.email,
      moment(r.addedOn).format('YYYY/MM/DD'),
      moment(r.checkOutDate).format('YYYY/MM/DD'),
      r.loyaltyId || 'Missing',
      r.isBonusGiven ? 'true' : 'false'
    ])

    copy(
      [titleRow, ...resAsArrays].map(lines => lines.join('\t')).join('\n'),
      'subscriptions table',
      false
    )
  }

  useEffect(() => {
    setIsSubscribersLoading(true)

    subscribersApi
      .getLastSubscribers()
      .then(data => {
        setUsesBonus(data.usesBonus)

        setLastSubscribers(data.subscriptions)

        setIsSubsscribersErred(false)
      })
      .catch(() => {
        setIsSubsscribersErred(true)
      })
      .finally(() => {
        setIsSubscribersLoading(false)
      })
  }, [])

  function handleClickLoyaltyIdMissingDialog() {
    setisLoyaltyIdMissingDialogVisible(false)
    setIsEditGuestDialogVisible(true)
  }

  function handleClickEditGuest(subscriber) {
    setActiveSubscriber(subscriber)
    setIsEditGuestDialogVisible(true)
  }

  function refetchSubscribers() {
    subscribersApi.getLastSubscribers().then(data => {
      setLastSubscribers(data.subscriptions)
      setIsSubsscribersErred(false)
    })
  }

  function handleEditGuest() {
    addToast(
      `Guest ${
        activeSubscriber.firstName
          ? `${activeSubscriber.firstName} ${activeSubscriber.lastName}`
          : ''
      }  has updated`,
      { appearance: 'success' }
    )

    setIsEditGuestDialogVisible(false)
    refetchSubscribers()
  }

  function handleBonusApplied() {
    addToast(
      `Bonus applied to ${
        activeSubscriber.firstName
          ? `${activeSubscriber.firstName} ${activeSubscriber.lastName}`
          : ''
      }`,
      { appearance: 'success' }
    )

    setIsBonusDialogVisible(false)
    refetchSubscribers()
  }

  function handleClickBonusToggle(subscriptionId) {
    const subscriberIndex = lastSubscribers.findIndex(
      s => s.subscriptionId === subscriptionId
    )

    let updatedSubscribers = [...lastSubscribers]

    updatedSubscribers[subscriberIndex] = {
      ...lastSubscribers[subscriberIndex],
      isBonusGiven: true
    }

    setLastSubscribers(updatedSubscribers)

    subscribersApi.applyBonus({ subscriberId: subscriptionId }).catch(() => {
      updatedSubscribers[subscriberIndex] = {
        ...lastSubscribers[subscriberIndex],
        isBonusGiven: false
      }

      addToast(`Something went wrong while setting bonus as applied`, {
        appearance: 'success'
      })

      setLastSubscribers(updatedSubscribers)
    })
  }

  const [sortByIndex, setSortByIndex] = useState(4)
  const [sortDecreasing, setSortDecreasing] = useState(false)

  function handleTitleCellClick(rowIndex) {
    if (sortByIndex === rowIndex) {
      setSortDecreasing(d => !d)
    }

    setSortByIndex(rowIndex)
  }

  function getSortedResults(lastSubscribers) {
    const sortedSubscribers = [...lastSubscribers]

    if (sortByIndex === 0) {
      sortedSubscribers.sort((a, b) => {
        if (a.firstName === null) {
          return 1
        }

        if (b.firstName === null) {
          return -1
        }

        if (a.firstName < b.firstName) {
          return -1
        }
        if (a.firstName > b.firstName) {
          return 1
        }

        return 0
      })
    }

    if (sortByIndex === 1) {
      sortedSubscribers.sort((a, b) => {
        if (a.phoneNumber === null) {
          return 1
        }

        if (b.phone === null) {
          return -1
        }

        return `${b.phone}` - `${a.phone}`
      })
    }

    if (sortByIndex === 2) {
      sortedSubscribers.sort((a, b) => {
        if (a.email === null) {
          return 1
        }

        if (b.email === null) {
          return -1
        }

        if (a.email < b.email) {
          return -1
        }
        if (a.email > b.email) {
          return 1
        }

        return 0
      })
    }

    if (sortByIndex === 3) {
      sortedSubscribers.sort((a, b) => {
        if (a.addedOn < b.addedOn) {
          return -1
        }

        if (a.addedOn > b.addedOn) {
          return 1
        }

        return 0
      })
    }

    if (sortByIndex === 4) {
      sortedSubscribers.sort((a, b) => {
        if (a.lastCheckOut < b.lastCheckOut) {
          return -1
        }

        if (a.lastCheckOut > b.lastCheckOut) {
          return 1
        }

        return 0
      })
    }

    if (sortByIndex === 5) {
      sortedSubscribers.sort((a, b) => {
        if (a.loyaltyId === null) {
          return 1
        }

        if (b.loyaltyId === null) {
          return -1
        }

        return `${b.loyaltyId}` - `${a.loyaltyId}`
      })
    }

    return sortDecreasing ? sortedSubscribers.reverse() : sortedSubscribers
  }

  return (
    <div>
      <CustomHeader title="Recent subscriptions" width={1180} isFixed />
      {isBonusDialogVisible && (
        <BonusDialog
          onClose={() => setIsBonusDialogVisible(false)}
          onSuccess={handleBonusApplied}
          {...activeSubscriber}
        />
      )}
      {isEditGuestDialogVisible && (
        <EditGuestDialog
          guest={activeSubscriber}
          onClose={() => setIsEditGuestDialogVisible(false)}
          onUpdate={() => handleEditGuest()}
        />
      )}
      {isSubscribersLoading && (
        <div className={styles.wrapper}>
          <LoadingIndicator />
        </div>
      )}
      {!isSubscribersLoading && isSubsscribersErred && (
        <div className={styles.noResults}>Something went wrong</div>
      )}
      {!isSubscribersLoading && !isSubsscribersErred && !lastSubscribers && (
        <div className={styles.noResults}>No subscribers found for last 10 days </div>
      )}
      {isLoyaltyIdMissingDialogVisible && (
        <ConfirmationDialog
          title={`Guest doesn't have ${loyaltyIdName}`}
          onClose={() => setisLoyaltyIdMissingDialogVisible(false)}
          onConfirm={handleClickLoyaltyIdMissingDialog}
          positiveMessage="Edit guest"
          negativeMessage="Cancel"
        >
          <p className={styles.dialogDescription}>
            You need to add a {loyaltyIdName} to apply bonus
          </p>
        </ConfirmationDialog>
      )}
      {!isSubscribersLoading && !isSubsscribersErred && lastSubscribers?.length > 0 && (
        <div className={styles.wrapper}>
          <div className={styles.filtersHolder}>
            <button
              className={
                isShowingWithMissingInfo ? styles.filterButtonActive : styles.filterButton
              }
              onClick={() => setIsShowingWithMissingInfo(v => !v)}
            >
              With missing info (
              {lastSubscribers.filter(s => s.isAnyMissingInformation).length})
            </button>
            <button
              className={
                isShowingPendingBonus ? styles.filterButtonActive : styles.filterButton
              }
              onClick={() => setIsShowingPendingBonus(v => !v)}
            >
              Pending bonus ({lastSubscribers.filter(s => !s.isBonusGiven).length})
            </button>
            <SecondaryRegularButton
              className={styles.copyToExcelButton}
              onClick={() => copyToExcel()}
            >
              <ExcelIcon /> Copy table to Excel
            </SecondaryRegularButton>
          </div>
          <Table
            widths={[210, 160, 210, 80, 140, 205, 90]}
            sortIndex={sortByIndex}
            sortDirection={sortDecreasing ? 'down' : 'up'}
            handleTitleCellClick={handleTitleCellClick}
          >
            <TitleRow>
              <TitleCell>Guest name</TitleCell>
              <TitleCell>Phone</TitleCell>
              <TitleCell>Email</TitleCell>
              <TitleCell>Added on</TitleCell>
              <TitleCell>Checkout date</TitleCell>
              <TitleCell>{loyaltyIdName}</TitleCell>
              <TitleCell>{usesBonus && 'Bonus applied'}</TitleCell>
              <TitleCell></TitleCell>
            </TitleRow>
            {getSortedResults(
              lastSubscribers
                .filter(s =>
                  isShowingWithMissingInfo ? s.isAnyMissingInformation : true
                )
                .filter(s => (isShowingPendingBonus ? s.isBonusGiven === false : true))
            ).map(s => (
              <Row className={styles.row} key={s.subscriptionId}>
                <Cell className={styles.fullNameCell}>
                  {s.firstName} {s.lastName}
                </Cell>
                <Cell className={styles.phoneCell}>{formatPhoneNumber(s.phone)}</Cell>
                <Cell className={styles.email}>
                  <span className={styles.emailText}>{s.email}</span>
                </Cell>
                <Cell>{moment(s.addedOn).format('MMM DD')}</Cell>
                <Cell>
                  {moment(s.lastCheckOut).isBefore(moment('2000-01-01')) ? (
                    <span className={styles.missingData}>Missing</span>
                  ) : (
                    moment(s.lastCheckOut).format('MMM DD, YYYY')
                  )}
                </Cell>
                <Cell>
                  <span className={styles.loyaltyId}>
                    {s.loyaltyId || <span className={styles.missingData}>Missing</span>}
                  </span>
                  {s.loyaltyId && (
                    <button
                      className={styles.copyButton}
                      onClick={() => copy(s.loyaltyId)}
                    >
                      <CopyIcon />
                      Copy
                    </button>
                  )}
                </Cell>
                <Cell>
                  {usesBonus && (
                    <div className={styles.toggle}>
                      <Toggle
                        checked={s.isBonusGiven}
                        handleChange={e =>
                          e.target.checked
                            ? handleClickBonusToggle(s.subscriptionId)
                            : false
                        }
                      />
                    </div>
                  )}
                </Cell>
                <Cell>
                  <button
                    onClick={() => handleClickEditGuest(s)}
                    className={styles.editButton}
                  >
                    Edit
                  </button>
                </Cell>
              </Row>
            ))}
          </Table>
        </div>
      )}
    </div>
  )
}

export default Subscribers
